/**
 * One-time removal of this browser's Web Push registration.
 *
 * Reminders left the web app, and push only ever carried reminders. The worker
 * still serves reminders to older builds, so a token left on the server would
 * keep receiving their pushes. Once per device: delete the server's token,
 * then drop the browser subscription. Until both succeed it runs again on the
 * next startup (and when the browser comes back online).
 *
 * The app never stored the token id the server assigned. `POST /device-tokens`
 * is an upsert on the token itself, so posting the same subscription again
 * returns that row's id, which `DELETE /device-tokens/:id` then removes.
 */

import { ApiError, apiFetchOrThrow } from '../api/client'
import { Sentry } from './sentry'

export const PUSH_UNREGISTERED_KEY = 'caliu-push-unregistered'

interface PushSubscriptionLike {
  unsubscribe(): Promise<boolean>
  toJSON(): unknown
}

export interface PushUnregisterDeps {
  /** The browser's current push subscription, or null (none, or no support). */
  getSubscription(): Promise<PushSubscriptionLike | null>
  request<T>(path: string, init: RequestInit): Promise<T>
  storage: Pick<Storage, 'getItem' | 'setItem'>
}

async function browserSubscription(): Promise<PushSubscriptionLike | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  if (typeof window === 'undefined' || !('PushManager' in window)) return null
  // `getRegistration` (not `ready`): with no service worker registered,
  // `ready` never settles, and then there is no subscription either.
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return null
  return registration.pushManager.getSubscription()
}

const browserDeps: PushUnregisterDeps = {
  getSubscription: browserSubscription,
  request: apiFetchOrThrow,
  get storage() {
    return localStorage
  },
}

class FailedFetch extends Error {
  constructor(readonly original: TypeError) {
    super(original.message)
    this.name = 'FailedFetch'
  }
}

/**
 * Returns true once this device is unregistered (now or on an earlier run),
 * false when it should be tried again. Signed out, there is no session to
 * delete the server token with: the subscription is dropped locally, which
 * kills its endpoint, so the server's pushes to it fail from then on.
 */
export async function unregisterPushOnce(
  signedIn: boolean,
  deps: PushUnregisterDeps = browserDeps,
): Promise<boolean> {
  if (deps.storage.getItem(PUSH_UNREGISTERED_KEY) === '1') return true
  // A TypeError rejected by the request itself is fetch failing (offline,
  // DNS, connection reset). Tag it so a TypeError from anything else here
  // still reaches Sentry.
  const request = <T>(path: string, init: RequestInit): Promise<T> =>
    deps.request<T>(path, init).catch((err: unknown) => {
      throw err instanceof TypeError ? new FailedFetch(err) : err
    })
  try {
    const subscription = await deps.getSubscription()
    if (subscription) {
      if (signedIn) {
        const res = await request<{ deviceToken?: { id?: string } } | undefined>('/device-tokens', {
          method: 'POST',
          body: JSON.stringify({ token: JSON.stringify(subscription), platform: 'web' }),
        })
        const id = res?.deviceToken?.id
        if (!id) {
          Sentry.captureException(new Error('POST /device-tokens returned no deviceToken.id'))
          return false
        }
        // A 404 already gone counts as success (apiFetchOrThrow returns undefined).
        await request(`/device-tokens/${encodeURIComponent(id)}`, { method: 'DELETE' })
      }
      await subscription.unsubscribe()
    }
    deps.storage.setItem(PUSH_UNREGISTERED_KEY, '1')
    return true
  } catch (err) {
    // Expected, so not reported: a failed fetch (offline), 401 (the session
    // ended; signed out next time it only unsubscribes) and 429 (try later).
    // Every other answer is not user input here, so any other 4xx and every
    // 5xx are reported, as is anything else. All of them retry next time.
    const expected =
      err instanceof FailedFetch ||
      (err instanceof ApiError && (err.status === 401 || err.status === 429))
    if (!expected) Sentry.captureException(err)
    return false
  }
}
